import "./index.scss";

import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
} from "@mui/material";
import { ReactComponent as IconSortVertical } from "assets/images/icons/moving-vertical.svg";
import { ReactComponent as IconTrash } from "assets/images/icons/trash.svg";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import Button from "components/Button";
import Input from "components/Input";
import ScreenEmptySection from "components/ScreenEmptySection";
import SearchableTreeList, { TSearchableTreeNodeItem } from "components/SearchableTreeList";
import _ from "lodash";
import { ExerciseByCircuit, ExerciseByMuscleGroup } from "models/lookups";
import { Protocol } from "models/protocol";
import { StrengthProgram, StrengthProgramExercise } from "models/workoutplan";
import { nanoid } from "nanoid";
import { memo, useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ReactSortable } from "react-sortablejs";
import { toast } from "react-toastify";

const StrengthProgramExercisesCreateEdit = (props: {
  open: boolean;
  onClose: any;
  isCreate: boolean;
  program: StrengthProgram;
  strengthExerciseProtocols: any[];
  exerciseByMuscleGroupEntries: ExerciseByMuscleGroup[];
  exerciseByCircuitEntries: ExerciseByCircuit[];
}) => {
  const {
    open,
    onClose,
    isCreate,
    program,
    strengthExerciseProtocols,
    exerciseByMuscleGroupEntries,
    exerciseByCircuitEntries,
  } = props;
  const defaultEmptyValue: StrengthProgram = {
    exercises: [],
    id: 0,
    is_active: 0,
    is_express: 0,
    name: "",
    per_week: 1,
    type: "STRENGTH",
  };
  const {
    getValues,
    formState: { errors },
    control,
    setValue,
    trigger,
    handleSubmit,
  } = useForm<StrengthProgram>({
    defaultValues: defaultEmptyValue,
  });
  const [currentProgram, setCurrentProgram] = useState<StrengthProgram>(defaultEmptyValue);
  const [exercisesList, setExercisesList] = useState(defaultEmptyValue.exercises);
  const [selectedProtocolText, setSelectedProtocolText] = useState(`Standard`);
  const standardProtocol = {
    description: "1 Set, 8 - 12 Reps",
    id: 1,
    is_fitlinxx_standard: true,
    min_drop_time: 100,
    min_drop_time_for_display: 1,
    min_lift_time: 100,
    min_lift_time_for_display: 1,
    rest_time: 0,
    sets: [{ id: 1, max_reps: 12, min_reps: 9, order: 1, status: "ACTIVE" }],
    title: "Standard",
  };
  const [selectedProtocol, setSelectedProtocol] = useState<Protocol>(standardProtocol);
  const [listExerciseBy, setListExerciseBy] = useState("circuit");
  const [exerciseByMuscleGroupOptions, setExerciseByMuscleGroupOptions] = useState<
    TSearchableTreeNodeItem[]
  >([]);
  const [exerciseByCircuitOptions, setExerciseByCircuitOptions] = useState<
    TSearchableTreeNodeItem[]
  >([]);

  const [programNameCount, setProgramNameCount] = useState(0);
  const programNameLength = 31;

  useEffect(() => {
    if (open) {
      if (!isCreate) {
        setCurrentProgram(program);
        setExercisesList(program.exercises);
        setValue("id", program.id);
        setValue("nanoId", program.nanoId);
        setValue("name", program.name);
        setValue("is_active", program.is_active);
        setValue("is_express", program.is_express);
        setValue("exercises", program.exercises);
      } else {
        setCurrentProgram(defaultEmptyValue);
        setExercisesList(defaultEmptyValue.exercises);
        setValue("id", defaultEmptyValue.id);
        setValue("nanoId", nanoid());
        setValue("name", defaultEmptyValue.name);
        setValue("is_active", defaultEmptyValue.is_active);
        setValue("is_express", defaultEmptyValue.is_express);
        setValue("exercises", defaultEmptyValue.exercises);
      }
    }
  }, [open]);

  useEffect(() => {
    //Update program array after sorting
    setValue("exercises", exercisesList);
  }, [exercisesList]);

  useEffect(() => {
    let tempArray = [];
    Object.keys(exerciseByMuscleGroupEntries).forEach((key) => {
      let entries = [];
      exerciseByMuscleGroupEntries[key].forEach((exercise) => {
        entries.push({ label: `${exercise.exercise_title}`, value: exercise.exercise_id });
      });
      tempArray.push({
        label: key,
        subItems: entries,
      });
    });
    setExerciseByMuscleGroupOptions(tempArray);
  }, [exerciseByMuscleGroupEntries]);

  useEffect(() => {
    let tempArray = [];
    exerciseByCircuitEntries.forEach((circuit) => {
      let entries = [];
      circuit.exercises.forEach((exercise) => {
        entries.push({ label: exercise.exercise_title, value: exercise.exercise_id });
      });
      tempArray.push({
        label: circuit.circuit_title,
        subItems: entries,
      });
    });
    setExerciseByCircuitOptions(tempArray);
  }, [exerciseByCircuitEntries]);

  //#region event handlers
  const handleCloseModal = useCallback(() => {
    onClose();
  }, []);

  const handleConfirm = () => {
    trigger().then((validationResult) => {
      if (!validationResult) {
        return;
      }
      for (let i = 0; i < exercisesList.length; i++) {
        let e = exercisesList[i];
        if (
          e.time_down_for_display > 10 ||
          e.time_down_for_display < 0 ||
          e.time_up_for_display > 10 ||
          e.time_up_for_display < 0
        ) {
          toast(`Min lift/drop time should be between 0 and 10`, { type: "error" });
          return;
        }
      }
      const exercises = getValues("exercises");
      if (exercises.length === 0) {
        toast(`No Strength Exercises addded`, { type: "info" });
      }
      onClose(getValues());
    });
  };

  const handleExerciseTimeValueUpdate = (itemId, value, upOrDown: "up" | "down") => {
    let copy = [...exercisesList];
    let indexOfEntry = copy.findIndex((e) => e.id === itemId);
    let attribute =
      upOrDown === "up"
        ? { time_up: +value * 100, time_up_for_display: +value }
        : { time_down: +value * 100, time_down_for_display: +value };
    let newEntry = { ...copy.find((e) => e.id === itemId), ...attribute };
    copy.splice(indexOfEntry, 1, newEntry);
    setExercisesList(copy);
  };

  const handleRemoveExercise = (itemId) => {
    let copy = [...exercisesList];
    const indexToDelete = copy.findIndex((e) => e.id === itemId);
    copy.splice(indexToDelete, 1);
    setExercisesList(copy);
  };

  const handleAddExercise = (exercise) => {
    if (exercisesList.findIndex((f) => f.id === exercise.value) === -1) {
      let entry: StrengthProgramExercise = {
        id: exercise.value,
        name: exercise.label,
        sets: selectedProtocol.sets.map((m) => ({
          reps_max: m.max_reps,
          reps_min: m.min_reps,
          rest_time: selectedProtocol.rest_time,
          weight: 0,
        })),
        settings: "",
        time_down: selectedProtocol.min_drop_time,
        time_down_for_display: selectedProtocol.min_drop_time_for_display,
        time_up: selectedProtocol.min_lift_time,
        time_up_for_display: selectedProtocol.min_lift_time_for_display,
      };
      setExercisesList([...exercisesList, entry]);
    } else {
      //This was introduced when we added checkbox to the tree list
      handleRemoveExercise(exercise.value);
    }
  };

  const handleAddExerciseGroup = (exercises) => {
    const result = [];
    exercises?.map((exercise) => {
      if (exercisesList.findIndex((f) => f.id === exercise.value) === -1) {
        let entry: StrengthProgramExercise = {
          id: exercise.value,
          name: exercise.label,
          sets: selectedProtocol.sets.map((m) => ({
            reps_max: m.max_reps,
            reps_min: m.min_reps,
            rest_time: selectedProtocol.rest_time,
          })),
          settings: "",
          time_down: selectedProtocol.min_drop_time,
          time_down_for_display: selectedProtocol.min_drop_time_for_display,
          time_up: selectedProtocol.min_lift_time,
          time_up_for_display: selectedProtocol.min_lift_time_for_display,
        };
        result.push(entry);
      }
    });
    setExercisesList([...exercisesList, ...result]);
  };
  //#endregion event handlers

  //function isStandardProtocol(proto) {
  //  return proto.id == 1;
  //}

  //console.log(`strengthExerciseProtocols:\n`, strengthExerciseProtocols);
  //console.log(`standardProtocol:\n`, standardProtocol);

  return (
    <Modal
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}
      open={open}
      onClose={handleCloseModal}
    >
      <>
        <form onSubmit={handleSubmit(handleConfirm)}>
          <Stack className="personal-details-modal-container program-exercise-create-edit">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              marginBottom="40px"
              className="header"
            >
              <h1>{isCreate ? `Create` : `Edit`} Program</h1>
              <DeleteIcon width="24px" height="24px" onClick={handleCloseModal} />
            </Stack>
            <h2 className="headerSpacing">Strength Program Name</h2>
            <Box mb={"5px"}>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value, ref } }) => (
                  <Stack spacing={1}>
                    <Input
                      value={value}
                      onChange={(v) => {
                        onChange(v);
                        setProgramNameCount(v.target.value.length);
                      }}
                      maxLength={programNameLength}
                      hasError={!_.isEmpty(errors.name)}
                      placeholder="Enter Name"
                    />
                    {errors.name?.type === "required" && (
                      <h4 className="error">This field is required</h4>
                    )}
                  </Stack>
                )}
              />
            </Box>
            <Stack
              mb={"32px"}
              direction={"row"}
              alignItems={"right"}
              justifyContent={"flex-end"}
              gap={1}
            >
              {programNameCount}/{programNameLength}
            </Stack>
            <h2 className="headerSpacing">Select Exercise to Add</h2>
            <Stack className="exercises-section" mb={`33px`} gap={"6px"}>
              <FormControl fullWidth>
                {/* Protocol */}
                <Select
                  value={selectedProtocolText}
                  MenuProps={{ classes: { paper: "strength-exercise-protocol-selector" } }}
                  onChange={(ee: any) => {
                    //const values = strengthExerciseProtocols[0];
                    //console.log(`strengthExerciseProtocols.id:\n`, ee.id);
                    //console.log(`strengthExerciseProtocols.pointerId\n`, ee.pointerId);
                    //console.log(`strengthExerciseProtocols.value:\n`, ee.target.value);
                    //console.log(`strengthExerciseProtocols choosen:\n`, ee);
                    setSelectedProtocolText(ee.target.value);
                  }}
                >
                  <MenuItem
                    value={`Standard`}
                    onClick={() => {
                      setSelectedProtocol(standardProtocol);
                    }}
                  >
                    <div>
                      <div className="title">Select Protocol</div>
                    </div>
                  </MenuItem>
                  {strengthExerciseProtocols.map((item, index) => (
                    <MenuItem
                      key={`exercise-protocol-${index}`}
                      value={`${item.title}`}
                      onClick={() => {
                        setSelectedProtocol(item);
                      }}
                    >
                      <div>
                        <div className="title">{item.title}</div>
                        <div className="setsReps">
                          {item.sets.length} Set{item.sets.length > 1 && `s`}
                          {item.sets.length > 1 &&
                            `, ${item.sets[0].min_reps}-${item.sets[0].max_reps} Reps`}
                        </div>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Select Exercise */}
              <FormControl fullWidth>
                <Select
                  value="nothing"
                  renderValue={(v) => (
                    <div>
                      <div className="title">Select Exercise</div>
                    </div>
                  )}
                  MenuProps={{ classes: { paper: "strength-exercise-protocol-selector" } }}
                  onChange={(ee: any) => {
                    setSelectedProtocolText(ee.target.value);
                  }}
                >
                  <Stack direction="row">
                    <RadioGroup
                      row
                      className="radios"
                      value={listExerciseBy}
                      onChange={(v) => setListExerciseBy(v.target.value)}
                    >
                      <FormControlLabel value="circuit" control={<Radio />} label="By Circuit" />
                      <FormControlLabel value="muscle" control={<Radio />} label="By Body Part" />
                    </RadioGroup>
                  </Stack>
                  <Box className="dropdownSearchableTreeList">
                    {listExerciseBy === "circuit" && (
                      <SearchableTreeList
                        options={exerciseByCircuitOptions}
                        callback={(item) => {
                          handleAddExercise(item);
                        }}
                        addGroupAction={handleAddExerciseGroup}
                        isSelectedCheckmarkDisplayed={true}
                        selectedValues={exercisesList.map((m) => m.id)}
                      />
                    )}
                    {listExerciseBy === "muscle" && (
                      <SearchableTreeList
                        options={exerciseByMuscleGroupOptions}
                        callback={(item) => {
                          handleAddExercise(item);
                        }}
                        isSelectedCheckmarkDisplayed={true}
                        selectedValues={exercisesList.map((m) => m.id)}
                      />
                    )}
                  </Box>
                </Select>
              </FormControl>
            </Stack>
            <h2 className="headerSpacing">Exercises List</h2>
            <Box className="exercises-section">
              <Grid container>
                <Grid item xs={7} className="header">
                  Exercises
                </Grid>
                <Grid item xs={2} className="header">
                  Min Lift Time
                </Grid>
                <Grid item xs={2} className="header">
                  Min Drop Time
                </Grid>
                <Grid item xs={1} className="header"></Grid>
              </Grid>
              <div className="sortableAreaContainer program-exercise-create-edit-scrollable">
                <ReactSortable list={exercisesList} setList={setExercisesList} animation={200}>
                  {exercisesList.map((item, index) => (
                    <Grid
                      container
                      alignItems={"center"}
                      className="exercise-entry"
                      key={`program_exercise_${index}`}
                    >
                      <Grid item xs={7}>
                        <IconSortVertical className="sort-handle" />
                        {item.name}
                      </Grid>
                      <Grid item xs={2} sx={{ whiteSpace: "nowrap" }}>
                        <input
                          value={item.time_up_for_display}
                          type="number"
                          min="0"
                          max="10"
                          onChange={(e) =>
                            handleExerciseTimeValueUpdate(item.id, e.target.value, "up")
                          }
                        />
                        &nbsp;sec
                      </Grid>
                      <Grid item xs={2} sx={{ whiteSpace: "nowrap" }}>
                        <input
                          value={item.time_down_for_display}
                          type="number"
                          min="0"
                          max="10"
                          onChange={(e) =>
                            handleExerciseTimeValueUpdate(item.id, e.target.value, "down")
                          }
                        />
                        &nbsp;sec
                      </Grid>
                      <Grid item xs={1}>
                        <IconTrash onClick={() => handleRemoveExercise(item.id)} />
                      </Grid>
                    </Grid>
                  ))}
                </ReactSortable>
                {!exercisesList.length && <ScreenEmptySection />}
              </div>
            </Box>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" marginTop="30px">
              <Stack direction="row" alignItems="center" spacing={3}>
                <Button label="Cancel" className="text-button" onClick={handleCloseModal} />
                <Button
                  label="Confirm"
                  type="button"
                  className="confirm-button"
                  onClick={() => handleConfirm()}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
      </>
    </Modal>
  );
};

export default memo(StrengthProgramExercisesCreateEdit);
